/**
 * Addressbook Redux Selectors
 */

import { createSelector } from '@reduxjs/toolkit'
import type { AddressbookState } from './addressbook.slice'
import type { Address, AddressType } from '../domain/types'

interface RootState {
  addressbook: AddressbookState
}

// ============================================================================
// Base Selectors
// ============================================================================

export const selectAddressbookState = (state: RootState) => state.addressbook
export const selectAddresses = (state: RootState) => state.addressbook.items
export const selectSelectedAddress = (state: RootState) => state.addressbook.selectedAddress
export const selectAddressStats = (state: RootState) => state.addressbook.stats
export const selectLocationTypes = (state: RootState) => state.addressbook.locationTypes
export const selectCountries = (state: RootState) => state.addressbook.countries
export const selectCities = (state: RootState) => state.addressbook.cities
export const selectRegions = (state: RootState) => state.addressbook.regions
export const selectAddressesFilter = (state: RootState) => state.addressbook.filter
export const selectAddressesPagination = (state: RootState) => state.addressbook.pagination
export const selectAddressbookLoading = (state: RootState) => state.addressbook.loading
export const selectAddressbookSubmitting = (state: RootState) => state.addressbook.isSubmitting
export const selectAddressbookError = (state: RootState) => state.addressbook.error

// ============================================================================
// Derived Selectors
// ============================================================================

/**
 * Select addresses filtered by current filter state
 * Note: Filtering is now done server-side via pagination API
 * This selector returns the items as-is (already filtered by the API)
 */
export const selectFilteredAddresses = createSelector(
  [selectAddresses],
  (addresses) => addresses
)

/**
 * Select addresses by type
 */
export const selectAddressesByType = (type: AddressType) =>
  createSelector([selectAddresses], addresses =>
    addresses.filter(a => a.type === type)
  )

/**
 * Select favorite addresses
 */
export const selectFavoriteAddresses = createSelector(
  [selectAddresses],
  addresses => addresses.filter(a => a.isFavorite)
)

/**
 * Select default address
 */
export const selectDefaultAddress = createSelector(
  [selectAddresses],
  addresses => addresses.find(a => a.isDefault) ?? null
)

/**
 * Select addresses grouped by type
 */
export const selectAddressesGroupedByType = createSelector(
  [selectAddresses],
  addresses => {
    const grouped: Record<AddressType, Address[]> = {
    customer: [],
    plant: [],
      site: [],
      office: [],
      warehouse: [],
      home: [],
    }

    addresses.forEach(address => {
      if (grouped[address.type]) {
        grouped[address.type].push(address)
      }
    })

    return grouped
  }
)

/**
 * Select unique cities from addresses
 */
export const selectUniqueCities = createSelector(
  [selectAddresses],
  addresses => {
    const cities = new Set(addresses.map(a => a.city))
    return Array.from(cities).sort()
  }
)

/**
 * Select address by ID
 */
export const selectAddressById = (id: string) =>
  createSelector([selectAddresses], addresses =>
    addresses.find(a => a.id === id) ?? null
  )

/**
 * Select if any addresses are loading
 */
export const selectIsLoading = createSelector(
  [selectAddressbookLoading],
  loading => loading
)

/**
 * Select if there's an error
 */
export const selectHasError = createSelector(
  [selectAddressbookError],
  error => error !== null
)

/**
 * Select address count by type
 */
export const selectAddressCountByType = createSelector(
  [selectAddresses],
  addresses => ({
    site: addresses.filter(a => a.type === 'site').length,
    office: addresses.filter(a => a.type === 'office').length,
    warehouse: addresses.filter(a => a.type === 'warehouse').length,
    home: addresses.filter(a => a.type === 'home').length,
    total: addresses.length,
  })
)

/**
 * Select recent addresses (sorted by updatedAt)
 */
export const selectRecentAddresses = (limit = 5) =>
  createSelector([selectAddresses], addresses =>
    [...addresses]
      .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
      .slice(0, limit)
  )
